
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Menu } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useUserRole } from "@/hooks/useUserRole";
import { useProfileCompletion } from "@/hooks/useProfileCompletion";

const NavBar = () => {
  const { items } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isAdmin, role } = useUserRole();
  const { isProfileComplete } = useProfileCompletion();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/account");
    setIsOpen(false);
  };

  const handleNavClick = () => {
    setIsOpen(false);
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const isEmployee = role === 'moderator' || role === 'order_manager' || role === 'content_manager';
  const hasOrderAccess = isAdmin || role === 'moderator' || role === 'order_manager';
  const hasProductAccess = isAdmin || role === 'moderator';
  const hasContentAccess = isAdmin || role === 'moderator' || role === 'content_manager';

  return (
    <header className="w-full border-b bg-white shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Link to="/home" className="flex items-center space-x-2">
            <img 
              src="/lovable-uploads/agroref_fav.ico"
              alt="Agroref Logo"
              className="h-10 w-10"
            />
            <span className="text-xl font-bold text-zinc-900">Agroref</span>
          </Link>
          
          <nav className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
            <Link to="/home" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              Accueil
            </Link>
            <Link to="/a-propos" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              À Propos
            </Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors inline-flex items-center gap-1 whitespace-nowrap">
                Produits
                <span className="text-xs">▼</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#2c2c2c] border-[#2c2c2c] z-50 min-w-[200px] p-1">
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/produits/lely" className="w-full flex items-center h-10 px-3">Lely</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/produits/boumatic" className="w-full flex items-center h-10 px-3">BouMatic</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/produits/mueller" className="w-full flex items-center h-10 px-3">Mueller</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/produits/liberchem" className="w-full flex items-center h-10 px-3">Liberchem</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors inline-flex items-center gap-1 whitespace-nowrap">
                Services
                <span className="text-xs">▼</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#2c2c2c] border-[#2c2c2c] z-50 min-w-[240px] p-1">
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/services/assistance" className="w-full flex items-center h-10 px-3">Assistance à la traite</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/services/inspection" className="w-full flex items-center h-10 px-3">Inspection du système de traite</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/services/pro-action" className="w-full flex items-center h-10 px-3">Rapports Pro Action</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-[#3c3c3c] focus:bg-[#3c3c3c] focus:text-white cursor-pointer !h-10 py-0">
                  <Link to="/services/urgence" className="w-full flex items-center h-10 px-3">Service d'urgence 24/7</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              Boutique
            </Link>

            <Link to="/projets" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              Projet et Événements
            </Link>
            <Link to="/blogue" className="px-3 py-2 text-sm font-medium text-zinc-900 hover:text-accent transition-colors whitespace-nowrap">
              Blogue
            </Link>
          </nav>

          <div className="flex items-center space-x-3">
            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] bg-white">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col space-y-4 mt-6">
                  <Link 
                    to="/home" 
                    className="text-base font-medium text-zinc-900 hover:text-accent transition-colors"
                    onClick={handleNavClick}
                  >
                    Accueil
                  </Link>
                  <Link 
                    to="/a-propos" 
                    className="text-base font-medium text-zinc-900 hover:text-accent transition-colors"
                    onClick={handleNavClick}
                  >
                    À Propos
                  </Link>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-zinc-600">Produits</p>
                    <Link 
                      to="/produits/lely" 
                      className="block pl-4 text-base text-zinc-900 hover:text-accent transition-colors"
                      onClick={handleNavClick}
                    >
                      Lely
                    </Link>
                    <Link 
                      to="/produits/boumatic" 
                      className="block pl-4 text-base text-zinc-900 hover:text-accent transition-colors"
                      onClick={handleNavClick}
                    >
                      BouMatic
                    </Link>
                    <Link 
                      to="/produits/mueller" 
                      className="block pl-4 text-base text-zinc-900 hover:text-accent transition-colors"
                      onClick={handleNavClick}
                    >
                      Mueller
                    </Link>
                    <Link 
                      to="/produits/liberchem" 
                      className="block pl-4 text-base text-zinc-900 hover:text-accent transition-colors"
                      onClick={handleNavClick}
                    >
                      Liberchem
                    </Link>
                  </div>


                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-zinc-600">Services</p>
                    <Link 
                      to="/services/assistance" 
                      className="block pl-4 text-base text-zinc-900 hover:text-accent transition-colors"
                      onClick={handleNavClick}
                    >
                      Assistance à la traite
                    </Link>
                    <Link 
                      to="/services/inspection" 
                      className="block pl-4 text-base text-zinc-900 hover:text-accent transition-colors"
                      onClick={handleNavClick}
                    >
                      Inspection du système de traite
                    </Link>
                    <Link 
                      to="/services/pro-action" 
                      className="block pl-4 text-base text-zinc-900 hover:text-accent transition-colors"
                      onClick={handleNavClick}
                    >
                      Rapports Pro Action
                    </Link>
                    <Link 
                      to="/services/urgence" 
                      className="block pl-4 text-base text-zinc-900 hover:text-accent transition-colors"
                      onClick={handleNavClick}
                    >
                      Service d'urgence 24/7
                    </Link>
                  </div>

                  <Link 
                    to="/" 
                    className="text-base font-medium text-zinc-900 hover:text-accent transition-colors"
                    onClick={handleNavClick}
                  >
                    Boutique
                  </Link>

                  <Link 
                    to="/projets" 
                    className="text-base font-medium text-zinc-900 hover:text-accent transition-colors"
                    onClick={handleNavClick}
                  >
                    Projet et Événements
                  </Link>
                  <Link 
                    to="/blogue" 
                    className="text-base font-medium text-zinc-900 hover:text-accent transition-colors"
                    onClick={handleNavClick}
                  >
                    Blogue
                  </Link>
                  <Link 
                    to="/contact" 
                    className="text-base font-medium text-zinc-900 hover:text-accent transition-colors"
                    onClick={handleNavClick}
                  >
                    Contact
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
            {user && isProfileComplete && (
              <Link to="/cart" className="relative rounded-full p-2 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors">
                <ShoppingCart className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-xs font-medium text-white">
                    {totalItems}
                  </span>
                )}
              </Link>
            )}

            {user ? (
              <>
                {isProfileComplete ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full">
                        <User className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => navigate("/account")}>
                        <User className="mr-2 h-4 w-4" />
                        Mon compte
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/order-history")}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Mes commandes
                      </DropdownMenuItem>
                      {(isAdmin || isEmployee) && (
                        <>
                          <DropdownMenuSeparator />
                          {hasOrderAccess && (
                            <DropdownMenuItem onClick={() => navigate("/admin/orders")}>
                              <span className="mr-2">📦</span>
                              Admin - Commandes
                            </DropdownMenuItem>
                          )}
                          {hasProductAccess && (
                            <DropdownMenuItem onClick={() => navigate("/admin/products")}>
                              <span className="mr-2">📦</span>
                              Admin - Produits
                            </DropdownMenuItem>
                          )}
                          {hasContentAccess && (
                            <>
                              <DropdownMenuItem onClick={() => navigate("/admin/blogs")}>
                                <span className="mr-2">📝</span>
                                Admin - Blogs
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate("/admin/projects")}>
                                <span className="mr-2">🏗️</span>
                                Admin - Projets
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate("/admin/events")}>
                                <span className="mr-2">📅</span>
                                Admin - Événements
                              </DropdownMenuItem>
                            </>
                          )}
                          {(isAdmin || role === 'moderator') && (
                            <DropdownMenuItem onClick={() => navigate("/admin/contact-messages")}>
                              <span className="mr-2">💬</span>
                              Admin - Messages
                            </DropdownMenuItem>
                          )}
                          {isAdmin && (
                            <DropdownMenuItem onClick={() => navigate("/admin/employees")}>
                              <span className="mr-2">👥</span>
                              Admin - Employés
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Se déconnecter
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button variant="ghost" size="sm" onClick={handleLogout} className="text-accent hover:text-accent/90">
                    <LogOut className="mr-2 h-4 w-4" />
                    Se déconnecter
                  </Button>
                )}
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate("/account")}>
                <User className="mr-2 h-4 w-4" />
                Connexion
              </Button>
            )}

          </div>
        </div>
      </div>
    </header>
  );
};

export default NavBar;
